import { $generateHtmlFromNodes } from '@lexical/html';
import type { EditorState, LexicalEditor } from 'lexical';
import { $getRoot, $insertNodes, $nodesOfType } from 'lexical';
import type { Attachment } from 'sr-types/lib/production/v1/graphql';
import { $createAttachmentReferenceNodeFromAttachment, AttachmentReferenceNode } from './AttachmentReferenceNode';

const isNonNullAttachmentReferenceNode = (node: AttachmentReferenceNode | null): node is AttachmentReferenceNode =>
    node !== null;

const createAttachmentReferenceNodes = (attachments: ReadonlyArray<Attachment>): AttachmentReferenceNode[] => {
    return [...attachments].map($createAttachmentReferenceNodeFromAttachment).filter(isNonNullAttachmentReferenceNode);
};

export const insertAttachmentReferencesIntoEditor = (
    editor: LexicalEditor,
    attachments: ReadonlyArray<Attachment>
): string => {
    let htmlContent = '';

    editor.update(
        () => {
            const attachmentReferenceNodes = createAttachmentReferenceNodes(attachments);

            if (attachmentReferenceNodes.length === 0) {
                return;
            }

            $insertNodes(attachmentReferenceNodes);
            htmlContent = $generateHtmlFromNodes(editor);
        },
        { discrete: true }
    );

    return htmlContent;
};

export const extractAttachmentReferenceIdsFromEditor = (editor: LexicalEditor): ReadonlySet<string> => {
    return extractAttachmentReferenceIdsFromEditorState(editor.getEditorState());
};

export const extractAttachmentReferenceIdsFromEditorState = (editorState: EditorState): ReadonlySet<string> => {
    const referenceIds = new Set<string>();

    editorState.read(() => {
        $nodesOfType(AttachmentReferenceNode).forEach((node) => {
            referenceIds.add(node.getReferenceId());
        });
    });

    return referenceIds;
};

export const generateAttachmentReferencesHtml = (
    attachments: ReadonlyArray<Attachment>,
    editor: LexicalEditor
): string => {
    if (attachments.length === 0) {
        return '';
    }

    // Create the Lexical nodes and add them to the editor state.
    let htmlString = '';

    editor.update(
        () => {
            const attachmentReferenceNodes = createAttachmentReferenceNodes(attachments);

            if (attachmentReferenceNodes.length === 0) {
                return;
            }

            const root = $getRoot();

            // Clear previous content and insert nodes via selection so inline nodes end up inside a paragraph.
            root.clear();
            root.selectEnd();
            $insertNodes(attachmentReferenceNodes);
        },
        { discrete: true }
    );

    // Serialize the finalized editor state to HTML.
    editor.getEditorState().read(() => {
        htmlString = $generateHtmlFromNodes(editor);
    });

    return htmlString;
};
